import React from 'react'
import { Box, Typography } from '@mui/material'

export interface SectionHeaderProps {
  /** Main section title */
  title: string
  /** Optional subtitle/description */
  subtitle?: string
  /** Optional action element (buttons, tabs, etc.) */
  action?: React.ReactNode
  /** Remove bottom margin */
  noMargin?: boolean
  /**
   * Optional `data-testid` for the header block and, separately, for its
   * subtitle. Passed in rather than derived from `title`, because the subtitle
   * this header is most often asked about ("Aggregated from N videos") carries
   * a live count — a test that located it by text would be asserting the
   * fixture's size in the same breath as the element's existence.
   */
  testId?: string
  subtitleTestId?: string
}

/**
 * SectionHeader Component
 *
 * Consistent section headers for the analytics dashboard.
 * Features a subtle bottom border and optional action slot.
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  action,
  noMargin = false,
  testId,
  subtitleTestId,
}) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      bgcolor: 'background.paper',
      px: 3,
      pt: 4.5,
      pb: 2.5,
      borderBottom: '1px solid',
      borderColor: 'rgba(0,0,0,0.06)',
      gap: 2,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'grey.900',
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          data-testid={subtitleTestId}
          sx={{
            color: 'text.secondary',
            mt: 0.25,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    {action && (
      <Box sx={{ flexShrink: 0 }}>
        {action}
      </Box>
    )}
  </Box>
)

export default SectionHeader
